import React, { useState, useEffect, useCallback, ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavigateFunction, Params, useNavigate, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';

import { ActivityDataTemplate } from '../../../../types/common.types';
import {
  ActivityEngagement,
  Comment,
  HashTag,
  HashtagEngagement,
  Post,
  Reaction,
  TribeTemplate
} from '../../../../types/entity.types';
import { RootState } from '../../../../types/state/root.types';
import { TribeState } from '../../../../types/state/tribe.types';

import { formatUsername } from '../../../../utils/util';
import { SHORT_MONTHS } from '../../../../utils/constants';

import {
  requestViewActivityEngagement,
  requestViewHashtagEngagement
} from '../../../../api/administration/administration.actions';
import { requestViewTribeTemplate } from '../../../../api/tribe-template/tribe-template.actions';

import TribeMemberEngagement from '../../../../components/screens/programme/tribe/tribe-member-engagement/TribeMemberEngagement';

const TribeMemberEngagementContainer = (): ReactElement => {
  const [activityData, setActivityData] = useState<ActivityDataTemplate[]>([]);
  const [hashtagData, setHashtagData] = useState<unknown[]>([]);

  const { activityEngagement, hashtagEngagement, tribeTemplate } = useSelector(
    ({ tribe }: RootState): TribeState => tribe
  );

  const dispatch: Dispatch = useDispatch();

  const { status, programmeExternalReference }: Readonly<Params> = useParams();

  const navigate: NavigateFunction = useNavigate();

  const mapActivityEngagement: (data: ActivityEngagement[]) => void = useCallback(
    (data: ActivityEngagement[]): void => {
      const mappedData: ActivityDataTemplate[] = data.map(
        (item: ActivityEngagement, index: number): ActivityDataTemplate => {
          const { userName, lastOnline, posts, reactions, comments } = item;

          const totalPosts: number = posts.length;
          const totalLikes: number = reactions.length;
          const totalComments: number = comments.length;

          return {
            '#': index + 1,
            username: formatUsername(userName),
            lastSeen: lastOnline ? formatDate(lastOnline) : '',
            postsCreated: totalPosts,
            likes: totalLikes,
            comments: totalComments,
            all: totalPosts + totalLikes + totalComments
          };
        }
      );

      setActivityData(mappedData);
    },
    []
  );

  const mapHashtagEngagement: (data: HashtagEngagement[]) => void = useCallback(
    (data: HashtagEngagement[]): void => {
      const { hashTags } = tribeTemplate as TribeTemplate;

      const mappedData: unknown[] = data.map((item: HashtagEngagement, index: number): unknown => {
        const mappedItem: Record<string, string | number> = {
          '#': index + 1,
          username: formatUsername(item.userName)
        };

        hashTags?.forEach((hashtag: HashTag): void => {
          const text: string = hashtag.text as string;
          mappedItem[text] = item.posts.filter((post: Post): boolean => !!post?.hashTag?.includes(text)).length;
        });

        return mappedItem;
      });

      setHashtagData(mappedData);
    },
    [tribeTemplate]
  );

  useEffect((): void => {
    // @ts-expect-error TODO: Fix after adding types to administration actions
    dispatch(requestViewActivityEngagement({ programmeExternalReference }));
    // @ts-expect-error TODO: Fix after adding types to tribe template actions
    dispatch(requestViewTribeTemplate({ externalReference: programmeExternalReference }));
    // @ts-expect-error TODO: Fix after adding types to administration actions
    dispatch(requestViewHashtagEngagement({ programmeExternalReference }));
  }, [dispatch, programmeExternalReference]);

  useEffect((): void => {
    if (activityEngagement.length > 0) {
      mapActivityEngagement(activityEngagement);
    }

    if (tribeTemplate && hashtagEngagement.length > 0) {
      mapHashtagEngagement(hashtagEngagement);
    }
  }, [activityEngagement, hashtagEngagement, tribeTemplate, mapActivityEngagement, mapHashtagEngagement]);

  const formatDate = (date: string): string => {
    if (!date) {
      return '';
    }

    const dateObj: Date = new Date(date);
    dateObj.setHours(dateObj.getHours() - 2);
    let day: number | string = dateObj.getDate();
    const month: string = SHORT_MONTHS[dateObj.getMonth()] as string;
    let hour: number | string = dateObj.getHours();
    let minute: number | string = dateObj.getMinutes();

    if (day < 10) {
      day = `0${day}`;
    }

    if (hour < 10) {
      hour = `0${hour}`;
    }

    if (minute < 10) {
      minute = `0${minute}`;
    }

    return `${day} ${month} ${hour}:${minute}`;
  };

  const applyDateFilter = (fromDate?: Date, toDate?: Date): void => {
    const copyOfActivityEngagement: ActivityEngagement[] = structuredClone(activityEngagement);
    copyOfActivityEngagement.forEach((item: ActivityEngagement): void => {
      item.posts = item.posts.filter((post: Post): boolean => filter(post, fromDate, toDate));
      item.comments = item.comments.filter((comment: Comment): boolean => filter(comment, fromDate, toDate));
      item.reactions = item.reactions.filter((reaction: Reaction): boolean => filter(reaction, fromDate, toDate));
    });
    mapActivityEngagement(copyOfActivityEngagement);

    const copyOfHashtagEngagement: HashtagEngagement[] = structuredClone(hashtagEngagement);
    copyOfHashtagEngagement.forEach((item: HashtagEngagement): void => {
      item.posts = item.posts.filter((post: Post): boolean => filter(post, fromDate, toDate));
    });
    mapHashtagEngagement(copyOfHashtagEngagement);
  };

  const filter = (item: Post | Comment | Reaction, fromDate?: Date, toDate?: Date): boolean => {
    const date: Date = new Date(item.startDate as string);
    if (fromDate && toDate) {
      return date >= fromDate && date <= toDate;
    }

    if (fromDate) {
      return date >= fromDate;
    }

    return !!toDate && date <= toDate;
  };

  const navigateBack = (): void => {
    navigate(`/programmes/${status}/${programmeExternalReference}/tribe`);
  };

  return (
    <TribeMemberEngagement
      activityData={activityData}
      hashtagData={hashtagData}
      hashtags={tribeTemplate?.hashTags}
      applyDateFilter={applyDateFilter}
      navigateBack={navigateBack}
    />
  );
};

export default TribeMemberEngagementContainer;
